import { ref, type Ref } from 'vue';
import { useStore } from 'vue-livestore';
import { events } from '@/livestore/schema.js';
import { signerApi, type SignRecordResponse } from '@/api/index.js';
import { ApiError } from '@/api/index.js';

// Per-record AuditRecord signing composable — replaces batch-signing for clock-in/out.
// Failures are accepted outcomes: a failed sign leaves the record unsigned (no jws_start/
// jws_combined set), not an error state that blocks the clock-in/out UX.

// All three signing calls below (signRecordStart/signRecordCombine/signAssignment) share this
// exact signing.value/error.value/try-catch-finally shell -- only the request body differs.
// Extracted once so a change to the error-handling contract (e.g. what counts as an "accepted
// outcome" failure) can't drift between the three call sites.
async function withSigningCall(
  signing: Ref<boolean>,
  error: Ref<string | null>,
  action: () => Promise<string>,
): Promise<string | null> {
  signing.value = true;
  error.value = null;
  try {
    return await action();
  } catch (e: unknown) {
    const status = e instanceof ApiError ? e.status : 0;
    const msg = e instanceof Error ? e.message : JSON.stringify(e);
    error.value = msg || `Signer responded ${status}`;
    // Accepted outcome: record stays unsigned, no retry queue
    return null;
  } finally {
    signing.value = false;
  }
}

/**
 * Sign a start-only JWS at clock-in time.
 * Stores the JWS in timeEntry.jws_start and sets audit_record_id + recorded_at.
 * On failure: returns null (record stays unsigned — this is normal).
 */
function signRecordStart(
  store: any,
  signing: Ref<boolean>,
  error: Ref<string | null>,
  entryId: string,
  {
    auditRecordId,
    clientId,
    startsAt,
  }: {
    auditRecordId: string;
    clientId: string;
    startsAt: number;
  },
): Promise<string | null> {
  return withSigningCall(signing, error, async () => {
    const nonce = crypto.randomUUID();
    const recordedAt = new Date().toISOString();
    const result: SignRecordResponse = await signerApi.signRecord({
      audit_record_id: auditRecordId,
      client_id: clientId,
      ends_at: null,
      kid: 'default',
      nonce,
      record_type: 'shift',
      recorded_at: recordedAt,
      starts_at: new Date(startsAt).toISOString(),
    });
    store.commit(
      events.auditRecordSigned({
        audit_record_id: auditRecordId,
        id: entryId,
        jws_combined: null,
        jws_start: result.jws,
        signed_at: Date.now(),
      }),
    );
    return result.jws;
  });
}

/**
 * Sign a combined JWS at clock-out time.
 * If jws_start exists: calls /sign/combine (two-step).
 * If jws_start is missing: falls back to /sign/record with ends_at only (alt-branch).
 * Stores result in jws_combined, clears jws_start.
 * On failure: returns null (record stays unsigned — this is normal).
 */
function signRecordCombine(
  store: any,
  signing: Ref<boolean>,
  error: Ref<string | null>,
  entryId: string,
  {
    auditRecordId,
    clientId,
    endsAt,
    jwsStart,
    startsAt,
  }: {
    auditRecordId: string;
    clientId: string;
    endsAt: number;
    jwsStart: string | null;
    startsAt: number | null;
  },
): Promise<string | null> {
  return withSigningCall(signing, error, async () => {
    const nonce = crypto.randomUUID();
    const recordedAt = new Date().toISOString();
    let result: SignRecordResponse;

    if (jwsStart) {
      // Primary path: combine start JWS with ends_at
      result = await signerApi.signCombine({
        audit_record_id: auditRecordId,
        client_id: clientId,
        ends_at: new Date(endsAt).toISOString(),
        kid: 'default',
        nonce,
        record_type: 'shift',
        recorded_at: recordedAt,
        start_jws: jwsStart,
      });
    } else {
      // Alt-path: no valid start JWS — sign ends_at alone
      result = await signerApi.signRecord({
        audit_record_id: auditRecordId,
        client_id: clientId,
        ends_at: new Date(endsAt).toISOString(),
        kid: 'default',
        nonce,
        record_type: 'shift',
        recorded_at: recordedAt,
        starts_at: null,
      });
    }

    store.commit(
      events.auditRecordSigned({
        audit_record_id: auditRecordId,
        id: entryId,
        jws_combined: result.jws,
        jws_start: null,
        signed_at: Date.now(),
      }),
    );
    return result.jws;
  });
}

/**
 * Sign a schedule assignment at distribution time. No drift check.
 * Returns the JWS string, or null on failure.
 */
function signAssignment(
  signing: Ref<boolean>,
  error: Ref<string | null>,
  {
    assignmentId,
    endsAt,
    memberEmail,
    startsAt,
    workplaceLabel,
  }: {
    assignmentId: string;
    endsAt: number;
    memberEmail: string;
    startsAt: number;
    workplaceLabel: string;
  },
): Promise<string | null> {
  return withSigningCall(signing, error, async () => {
    const nonce = crypto.randomUUID();
    const result = await signerApi.signAssignment({
      assignment_id: assignmentId,
      ends_at: new Date(endsAt).toISOString(),
      kid: 'default',
      member_email: memberEmail,
      nonce,
      record_type: 'schedule',
      starts_at: new Date(startsAt).toISOString(),
      workplace_label: workplaceLabel,
    });
    return result.jws;
  });
}

export function useAuditSigning() {
  const { store } = useStore();
  const signing = ref(false);
  const error = ref<string | null>(null);

  return {
    error,
    signAssignment: (args: {
      assignmentId: string;
      endsAt: number;
      memberEmail: string;
      startsAt: number;
      workplaceLabel: string;
    }) => signAssignment(signing, error, args),
    signing,
    signRecordCombine: (
      entryId: string,
      args: {
        auditRecordId: string;
        clientId: string;
        endsAt: number;
        jwsStart: string | null;
        startsAt: number | null;
      },
    ) => signRecordCombine(store, signing, error, entryId, args),
    signRecordStart: (
      entryId: string,
      args: { auditRecordId: string; clientId: string; startsAt: number },
    ) => signRecordStart(store, signing, error, entryId, args),
  };
}
